
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { StageAdminService } from '@shared-angular/services/stage-admin.service';
import { AdminStageDef } from '@shared/index';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { BulkAction, BulkActionBarComponent } from 'src/app/shared/components/bulk-action-bar/bulk-action-bar.component';
import { BlocklyStorageService } from 'src/app/shared/services/blockly-storage.service';
import { runBotBatch } from 'src/app/shared/services/bot-bulk-actions';
import { EditorItemsService } from 'src/app/shared/services/editor-items.service';
import { ManualLinkComponent } from "../../../../../../../shared/components/rag-admin/manual-link/manual-link.component";
import { StageManagerDetailComponent } from "./stage-manager-detail/stage-manager-detail.component";

@Component({
  selector: 'app-stage-manager',
  imports: [
    FormsModule,
    ManualLinkComponent,
    MessageModule,
    CardModule,
    InputTextModule,
    ButtonModule,
    BadgeModule,
    TableModule,
    MenuModule,
    DrawerModule,
    ConfirmDialogModule,
    TooltipModule,
    StageManagerDetailComponent,
    BulkActionBarComponent
  ],
  providers: [ConfirmationService],
  templateUrl: './stage-manager.component.html',
  styleUrl: './stage-manager.component.scss'
})
export class StageManagerComponent implements OnInit {
  public get bulkActions(): BulkAction[] {
    return [
      {
        id: 'bulk-delete',
        label: $localize`:@@bulk-delete:Löschen`,
        icon: 'ri-delete-bin-line',
        disabled: this.bulkBusy,
        command: () => this.confirmBulkDelete()
      }
    ];
  }


  public stageTitle: string = "";
  private readonly stageMenuItems = new Map<string, MenuItem[]>();
  public selectedStages: AdminStageDef[] = [];
  public bulkBusy = false;
  public bulkFailedNames = '';

  constructor(
    protected stageAdmin: StageAdminService,
    // Sonst werden die neuen Stages in Blockly komisch angezeigt
    private blocklyStorageService: BlocklyStorageService,
    private editorItemService: EditorItemsService,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService
  ) { }

  public ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['openStage']) {
        this.showDetailDrawer(params['openStage'] as string);
      }
    });
  }

  protected async duplicateStage(stageId: string) {
    await this.stageAdmin.duplicateStage(stageId);
    await this.blocklyStorageService.loadBlocklyStages();
    this.editorItemService.loadEditorItems();
  }

  public async createStage(): Promise<void> {
    const title = this.stageTitle.trim();
    if (title === '') return;
    this.stageTitle = '';
    const newStage = await this.stageAdmin.createStage(title);
    this.showDetailDrawer(newStage._id);
  }

  protected getStageMenuItems(stageId: string): MenuItem[] {
    const existingItems = this.stageMenuItems.get(stageId);
    if (existingItems) {
      return existingItems;
    }

    const items: MenuItem[] = [
      {
        label: $localize`:@@edit:Bearbeiten`,
        icon: 'ri-pencil-fill',
        command: () => {
          this.showDetailDrawer(stageId);
        }
      },
      {
        label: $localize`:@@duplicate:Duplizieren`,
        icon: 'ri-file-copy-2-fill',
        command: () => {
          void this.duplicateStage(stageId);
        }
      },
      {
        label: $localize`:@@delete:Löschen`,
        icon: 'ri-close-large-fill',
        command: () => {
          void this.stageAdmin.deleteStage(stageId);
        },
        styleClass: 'text-danger'
      }
    ];

    this.stageMenuItems.set(stageId, items);
    return items;
  }

  public confirmBulkDelete(): void {
    if (this.bulkBusy || !this.selectedStages.length) return;
    const toDelete = [...this.selectedStages];
    const count = toDelete.length;
    this.confirmationService.confirm({
      header: $localize`:@@bulk-delete-stages-header:${count}:count: Stage(s) löschen?`,
      icon: 'ri-error-warning-line',
      message: $localize`:@@bulk-delete-stages-message:Möchtest du ${count}:count: Stage(s) wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      rejectButtonProps: {
        label: $localize`:@@delete-reject:Abbrechen`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@delete-accept:Löschen`,
        severity: 'danger'
      },
      accept: async () => {
        if (this.bulkBusy) return;
        this.bulkBusy = true;
        this.bulkFailedNames = '';
        try {
          const result = await runBotBatch(toDelete, id => this.stageAdmin.deleteStage(id));
          this.selectedStages = result.failed;
          this.bulkFailedNames = result.failed.map(stage => stage.title).join(', ');
        } finally {
          this.bulkBusy = false;
        }
      }
    });
  }

  public detailDrawer: boolean = false;
  public selectedStageId?: string;

  public showDetailDrawer(id: string): void {
    this.selectedStageId = id;
    this.detailDrawer = true;
  }

  protected get positionsNeedNormalization(): boolean {
    return this.stageAdmin.stages.some((stage, index) => stage.sortFloat !== index + 1);
  }

  public async onRowReorder(): Promise<void> {
    // Positionsnummern werden erst hier neu vergeben, nicht schon beim Laden der Stages
    const stages = this.stageAdmin.stages;
    for (let i = 0; i < stages.length; i++) {
      const stage = stages[i];
      if (!stage) continue;
      const newPosition = i + 1;
      if (stage.sortFloat !== newPosition) {
        stage.sortFloat = newPosition;
        await this.stageAdmin.updateStagesField(stage._id, 'sortFloat', newPosition);
      }
    }
  }
}
